import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatDateTime } from '../../utils/dateUtils';
import {
    Alert, Box, Button, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { useToast } from '../../context/ToastContext';
import {
    JOURNAL_IMPORT_TEMPLATE,
    JournalImportBatch,
    JournalImportReport,
    JournalImportService,
} from '../../services/dataExchange.service';
import { downloadCsv } from '../../utils/csv';
import { formatMoney } from '../../utils/format';

/**
 * Journal CSV import: pick a file, dry-run it (no writes, row errors listed), then commit. Commit creates
 * DRAFT journals only, grouped by entry_ref in one batch; posting follows the normal maker-checker flow.
 */
const JournalImport: React.FC = () => {
    const toast = useToast();
    const fileInput = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [report, setReport] = useState<JournalImportReport | null>(null);
    const [batches, setBatches] = useState<JournalImportBatch[]>([]);
    const [busy, setBusy] = useState(false);

    const loadBatches = useCallback(async () => {
        try {
            setBatches(await JournalImportService.listBatches());
        } catch (e) {
            toast.showApiError(e, 'Failed to load import batches');
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadBatches(); }, [loadBatches]);

    const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const picked = e.target.files?.[0] ?? null;
        e.target.value = '';
        setReport(null);
        setFile(picked);
        if (!picked) return;
        setBusy(true);
        try {
            setReport(await JournalImportService.dryRun(picked));
        } catch (err) {
            toast.showApiError(err, 'Dry-run failed');
        } finally {
            setBusy(false);
        }
    };

    const canCommit = !!report && report.batchErrors.length === 0 && report.entryCount > 0
        && report.validEntries === report.entryCount && report.errorRows === 0;

    const commit = async (allowDuplicate = false) => {
        if (!file || !canCommit || busy) return;
        setBusy(true);
        try {
            const res = await JournalImportService.commit(file, allowDuplicate);
            toast.showSuccess(`Created ${res.entryCount} draft journal entries`);
            setFile(null);
            setReport(null);
            await loadBatches();
        } catch (err) {
            const res = (err as { response?: { status?: number; data?: unknown } })?.response;
            if (!allowDuplicate && res?.status === 409 && typeof res.data === 'string' && res.data.includes('allowDuplicate')
                && window.confirm(`${res.data.split('. Re-send')[0]}. Import it again anyway?`)) {
                setBusy(false);
                await commit(true);
                return;
            }
            toast.showApiError(err, 'Import failed');
        } finally {
            setBusy(false);
        }
    };

    const removeBatch = async (b: JournalImportBatch) => {
        if (!window.confirm(`Delete the ${b.draftCount} unposted draft entries of this batch?`)) return;
        try {
            await JournalImportService.deleteBatch(b.id);
            toast.showSuccess('Batch deleted');
            await loadBatches();
        } catch (err) {
            toast.showApiError(err, 'Could not delete the batch');
        }
    };

    return (
        <Box data-help-page="accounting.journalImport.page" sx={{ p: { xs: 1, sm: 3 } }}>
            <Typography variant="h5" gutterBottom>Journal Import</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Columns: entry_ref, date (yyyy-MM-dd), account_code, debit, credit, description, segment_code, department_code, project_code (optional)
                (optional; matched to a department's cost-centre code first, then its name). Rows with the same
                entry_ref form one journal entry. Import creates drafts only; they are posted from the journal list as usual.
            </Typography>

            <Box data-help-section="accounting.journalImport.controls" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Button data-help="accounting.journalImport.template" variant="outlined" startIcon={<FileDownloadIcon />}
                    onClick={() => downloadCsv('journal-import-template.csv', JOURNAL_IMPORT_TEMPLATE.headers, JOURNAL_IMPORT_TEMPLATE.rows)}>
                    Download template
                </Button>
                <input ref={fileInput} data-testid="journal-import-file" type="file" accept=".csv,text/csv" hidden onChange={onPick} />
                <Button data-help="accounting.journalImport.check" variant="contained" startIcon={<CloudUploadIcon />} disabled={busy} onClick={() => fileInput.current?.click()}>
                    Choose CSV and check
                </Button>
                <Button data-help="accounting.journalImport.commit" variant="contained" color="success" disabled={!canCommit || busy} onClick={() => commit()}>
                    Create draft entries
                </Button>
            </Box>

            {report && (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        {file?.name}: {report.totalRows} rows, {report.entryCount} entries, {report.validEntries} valid,{' '}
                        {report.errorRows} rows with errors
                    </Typography>
                    {report.batchErrors.map(e => <Alert key={e} severity="error" sx={{ mb: 1 }}>{e}</Alert>)}
                    {report.entries.length > 0 && (
                        <TableContainer>
                            <Table data-help-section="accounting.journalImport.checkTable" size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell data-help="accounting.journalImport.check.col.ref">Entry ref</TableCell><TableCell data-help="accounting.journalImport.check.col.date">Date</TableCell>
                                        <TableCell data-help="accounting.journalImport.check.col.lines" align="right">Lines</TableCell>
                                        <TableCell data-help="accounting.journalImport.check.col.debit" align="right">Debit</TableCell><TableCell data-help="accounting.journalImport.check.col.credit" align="right">Credit</TableCell>
                                        <TableCell data-help="accounting.journalImport.check.col.status">Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {report.entries.map(en => (
                                        <TableRow key={en.entryRef}>
                                            <TableCell>{en.entryRef}</TableCell>
                                            <TableCell>{en.date ?? ''}</TableCell>
                                            <TableCell align="right">{en.lineCount}</TableCell>
                                            <TableCell align="right">{formatMoney(en.totalDebit)}</TableCell>
                                            <TableCell align="right">{formatMoney(en.totalCredit)}</TableCell>
                                            <TableCell>
                                                {en.valid ? <Chip size="small" color="success" label="OK" />
                                                    : <Chip size="small" color="error" label="Has errors" />}
                                                {en.errors.map(er => (
                                                    <Typography key={er} variant="caption" color="error" display="block">{er}</Typography>
                                                ))}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    {report.rows.some(r => !r.valid) && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2">Row errors</Typography>
                            {report.rows.filter(r => !r.valid).map(r => (
                                <Typography key={r.rowNumber} variant="body2" color="error">
                                    Row {r.rowNumber} ({r.entryRef || 'no ref'}): {r.errors.join('; ')}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </Paper>
            )}

            <Typography variant="h6" gutterBottom>Import batches</Typography>
            <TableContainer data-help-section="accounting.journalImport.batches" component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="accounting.journalImport.batch.col.file">File</TableCell><TableCell data-help="accounting.journalImport.batch.col.imported">Imported</TableCell>
                            <TableCell data-help="accounting.journalImport.batch.col.entries" align="right">Entries</TableCell><TableCell data-help="accounting.journalImport.batch.col.draft" align="right">Draft</TableCell>
                            <TableCell data-help="accounting.journalImport.batch.col.posted" align="right">Posted</TableCell><TableCell data-help="accounting.journalImport.batch.col.status">Status</TableCell><TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {batches.length === 0 && (
                            <TableRow><TableCell colSpan={7}><Typography color="text.secondary">No imports yet</Typography></TableCell></TableRow>
                        )}
                        {batches.map(b => (
                            <TableRow key={b.id}>
                                <TableCell>{b.fileName}</TableCell>
                                <TableCell>{b.createdAt ? formatDateTime(b.createdAt) : ''}</TableCell>
                                <TableCell align="right">{b.entryCount}</TableCell>
                                <TableCell align="right">{b.draftCount}</TableCell>
                                <TableCell align="right">{b.postedCount}</TableCell>
                                <TableCell>{b.status}</TableCell>
                                <TableCell align="right">
                                    {b.status === 'COMMITTED' && (
                                        <Button data-help="accounting.journalImport.action.deleteDrafts" size="small" color="error" disabled={b.postedCount > 0}
                                            onClick={() => removeBatch(b)}>Delete drafts</Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default JournalImport;
